import { isMetaChannel, isWindowGated, type Channel } from "./types";

export const HOURS = 3_600_000;

/**
 * One day as a unit conversion, not a threshold. core-duplication/
 * day-ms-declared-eight-times: graduation.ts and freshness.ts each declared
 * a private copy in a different notation, and campaignRoi.ts spelled it
 * inline; all three import this one now.
 */
export const DAY_MS = 24 * HOURS;

/** Meta standard messaging window. Composer §6. T3: never agent-tunable. */
export const STANDARD_WINDOW_MS = 24 * HOURS;

/**
 * F-01, ruled (CLAUDE.md section 13, Composer section 6): `grace` is the final
 * stretch BEFORE the 24h window closes. It grants nothing `in_window` does
 * not; it exists to prioritize contact capture before the door shuts. There
 * is no leniency after 24h, ever. So grace only narrows behaviour earlier, by
 * telling the caller the window is about to shut.
 *
 * The 2h length (F-01): RATIFIED by Kabir 2026-09-27 (decision list item 24,
 * typed "ratify"). It was implemented at the ruled value and listed in
 * section 13 as awaiting; it is settled now.
 */
export const GRACE_WINDOW_MS = 2 * HOURS;

export type WindowState = "in_window" | "grace" | "closed";

export interface WindowClockInput {
  /** Timestamp of the customer's most recent inbound message. Null = never. */
  readonly lastCustomerMessageAt: Date | null;
  readonly now: Date;
  readonly standardWindowMs?: number;
  readonly graceWindowMs?: number;
}

/** What the platform is trying to send. */
export type MessageKind = "freeform" | "marketing";

export interface SendIntent {
  readonly kind: MessageKind;
  readonly channel: Channel;
  /** Marketing Messages opt-in captured for this contact. */
  readonly hasMarketingOptIn: boolean;
}

export type WindowGateReason =
  | "in_window"
  | "grace_window_closing"
  | "window_closed_freeform_forbidden"
  | "marketing_requires_optin"
  | "marketing_optin_present"
  | "no_inbound_message_yet"
  | "window_clock_unreadable"
  | "not_meta_channel_window_not_applicable"
  | "public_comment_window_not_applicable";

export interface WindowGateResult {
  readonly allowed: boolean;
  readonly state: WindowState;
  readonly reason: WindowGateReason;
  /** True in the tail of the window: prefer contact capture and wrap-up. */
  readonly closingSoon: boolean;
  /** Concrete routes when blocked. Never leave a caller with only a "no". */
  readonly alternatives: readonly string[];
}

/**
 * TA-1377 (finding 5): true when the clock cannot be read, an Invalid Date on
 * either side or a window length that is not a finite number. Every NaN
 * comparison is false, so such a clock used to fall through every branch of
 * `windowState` to `in_window`, and the gate allowed a free-form DM on it.
 * A null `lastCustomerMessageAt` is not unreadable: it is the ruled "never
 * messaged", closed with its own reason.
 */
export function windowClockUnreadable(input: WindowClockInput): boolean {
  const { lastCustomerMessageAt, now } = input;
  const standard = input.standardWindowMs ?? STANDARD_WINDOW_MS;
  const grace = input.graceWindowMs ?? GRACE_WINDOW_MS;
  return (
    !Number.isFinite(now.getTime()) ||
    (lastCustomerMessageAt !== null && !Number.isFinite(lastCustomerMessageAt.getTime())) ||
    !Number.isFinite(standard) ||
    !Number.isFinite(grace)
  );
}

/**
 * Pure function. No clock access, no IO. The whole point is that the one gate
 * standing between us and a platform ban is trivially testable.
 */
export function windowState(input: WindowClockInput): WindowState {
  const { lastCustomerMessageAt, now } = input;
  const standard = input.standardWindowMs ?? STANDARD_WINDOW_MS;
  const grace = input.graceWindowMs ?? GRACE_WINDOW_MS;

  // No inbound message means no window was ever opened. Closed, not open:
  // the window is a permission granted BY the customer, never a default.
  if (lastCustomerMessageAt === null) return "closed";

  // TA-1377: a clock nobody can read is closed, not open, and not a throw
  // either, for the reason the skew branch below gives. The 24h, the grace
  // and every ruled value are unchanged; this only closes a fail-open.
  if (windowClockUnreadable(input)) return "closed";

  const elapsed = now.getTime() - lastCustomerMessageAt.getTime();

  // Clock skew, or a webhook arriving before our own clock catches up. Treat a
  // future inbound as a fresh one rather than throwing: a hard gate that
  // crashes is a gate that gets caught in a try/catch and bypassed.
  if (elapsed < 0) return "in_window";

  if (elapsed >= standard) return "closed";
  if (elapsed >= standard - grace) return "grace";
  return "in_window";
}

/**
 * The hard gate. Every outbound intent passes through this before anything
 * else in the send path. Blocking is the safe default in every ambiguous case.
 */
export function evaluateWindowGate(
  clock: WindowClockInput,
  intent: SendIntent,
): WindowGateResult {
  const state = windowState(clock);
  const closingSoon = state === "grace";

  // TA-1377: an unreadable clock refuses on every channel and every kind,
  // ahead of the not-applicable reasons and the marketing opt-in, with its own
  // reason so the operator reads a data defect, never a routine closure
  // (section 0). An opt-in buys a send outside the window, not on a clock
  // nobody can read.
  if (windowClockUnreadable(clock)) {
    return {
      allowed: false,
      state: "closed",
      reason: "window_clock_unreadable",
      closingSoon: false,
      alternatives: [
        "route_to_sms_or_email_if_contact_captured",
        "collect_marketing_message_optin",
        "wait_for_customer_initiated_trigger",
      ],
    };
  }

  // Rails we own (SMS, email, webchat) are not under Meta's window, and
  // neither are public comments, which are Meta's but are not conversations
  // (TA-48; the vault does not state the comment exemption in so many words).
  //
  // F-20, ruled: SMS and email have their own consent regime (express consent
  // at capture, quiet hours, STOP handling), specced and reviewed before Phase
  // B ships; until then this gate returns an explicit "not applicable", never
  // a bare pass. The reason names that nothing was gated, so the absence of a
  // check is visible at the call site instead of silently reading as approval.
  //
  // Comments get their own reason. They are a Meta surface, so "not Meta"
  // would mislabel them, and the question left unanswered is a different one:
  // not a consent regime, but whether a public reply is ratified at all.
  // Neither reason is permission; outage.ts refuses both.
  if (!isWindowGated(intent.channel)) {
    return {
      allowed: true,
      state,
      reason: isMetaChannel(intent.channel)
        ? "public_comment_window_not_applicable"
        : "not_meta_channel_window_not_applicable",
      closingSoon,
      alternatives: [],
    };
  }

  if (state === "closed") {
    if (intent.kind === "marketing" && intent.hasMarketingOptIn) {
      return {
        allowed: true,
        state,
        reason: "marketing_optin_present",
        closingSoon: false,
        alternatives: [],
      };
    }

    const reason: WindowGateReason =
      intent.kind === "marketing"
        ? "marketing_requires_optin"
        : clock.lastCustomerMessageAt === null
          ? "no_inbound_message_yet"
          : "window_closed_freeform_forbidden";

    return {
      allowed: false,
      state,
      reason,
      closingSoon: false,
      alternatives: [
        "route_to_sms_or_email_if_contact_captured",
        "collect_marketing_message_optin",
        "wait_for_customer_initiated_trigger",
      ],
    };
  }

  // Inside the window (including grace) free-form is permitted, and so is
  // promotional content: Meta's 24h window allows it without a separate opt-in.
  return {
    allowed: true,
    state,
    reason: state === "grace" ? "grace_window_closing" : "in_window",
    closingSoon,
    alternatives: [],
  };
}
